import type { EnergyFlowWidget, FlowLink, FlowNode } from '../../../widgets';
import {
  BindingRow,
  ColourField,
  Field,
  ListEditor,
  OptionalBindingRow,
  Row,
  Section,
  TextField,
  patcher,
  type WidgetEditorProps,
} from '../fields';

/**
 * Energiefluss. Erst die Knoten, dann die Verbindungen, weil eine Verbindung
 * ohne Knoten nirgendwohin zeigt.
 *
 * Die Knotenauswahl in der Verbindung ist eine Liste der vorhandenen Knoten
 * und kein Textfeld: eine Kante auf eine Knoten-Id, die es nicht gibt,
 * zeichnet der Renderer einfach nicht, und suchen laesst sich das dann nur
 * im JSON.
 */
export function EnergyFlowEditor(
  { widget, plantId, missing, onChange }: WidgetEditorProps<EnergyFlowWidget>,
) {
  const patch = patcher(widget, onChange);
  const nodeOptions = widget.nodes.map((n) => ({ value: n.id, label: n.label || n.id }));

  return (
    <>
      <Section title="Anzeige">
        <Row>
          <OptionalBindingRow
            label="Arbeitszahl (COP)"
            value={widget.copPoint}
            plantId={plantId}
            filter={{ valueType: 'number' }}
            hint="Wird als große Zahl über den Fluss gelegt."
            onChange={(next) => patch({ copPoint: next })}
          />
          <TextField
            label="Einheit der Leistung"
            value={widget.powerUnit}
            placeholder="kW"
            onChange={(next) => patch({ powerUnit: next })}
          />
        </Row>
      </Section>

      <Section title="Knoten">
        <ListEditor<FlowNode>
          label="Knoten"
          required
          missing={missing.has('nodes')}
          items={widget.nodes}
          addLabel="Knoten"
          emptyHint="Mindestens zwei Knoten, sonst fließt nichts."
          create={() => ({ id: freshNodeId(widget.nodes), label: '' })}
          itemTitle={(n) => n.label || n.id}
          onChange={(next) => patch({ nodes: next })}
          renderItem={(node, patchNode) => (
            <>
              <Row>
                <TextField
                  label="Id"
                  value={node.id}
                  keepEmptyString
                  required
                  hint="Wird von den Verbindungen referenziert."
                  onChange={(next) => patchNode({ id: next ?? '' })}
                />
                <TextField
                  label="Beschriftung"
                  value={node.label}
                  keepEmptyString
                  required
                  placeholder="Netz"
                  onChange={(next) => patchNode({ label: next ?? '' })}
                />
              </Row>
              <ColourField
                label="Farbe"
                value={node.colour}
                onChange={(next) => patchNode({ colour: next })}
              />
            </>
          )}
        />
      </Section>

      <Section title="Verbindungen">
        <ListEditor<FlowLink>
          label="Verbindungen"
          required
          missing={missing.has('links')}
          items={widget.links}
          addLabel="Verbindung"
          emptyHint="Eine Verbindung braucht zwei Knoten und einen Datenpunkt, der ihre Dicke treibt."
          create={() => ({
            fromNodeId: widget.nodes[0]?.id ?? '',
            toNodeId: widget.nodes[1]?.id ?? '',
            valuePoint: '',
          })}
          itemTitle={(l) => `${labelOf(widget.nodes, l.fromNodeId)} nach ${labelOf(widget.nodes, l.toNodeId)}`}
          onChange={(next) => patch({ links: next })}
          renderItem={(link, patchLink) => (
            <>
              <Row>
                <Field label="Von" required>
                  <select
                    class="hv-pf__input"
                    value={link.fromNodeId}
                    onChange={(e) => patchLink({
                      fromNodeId: (e.currentTarget as HTMLSelectElement).value,
                    })}
                  >
                    <option value="">bitte wählen</option>
                    {nodeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Nach" required>
                  <select
                    class="hv-pf__input"
                    value={link.toNodeId}
                    onChange={(e) => patchLink({
                      toNodeId: (e.currentTarget as HTMLSelectElement).value,
                    })}
                  >
                    <option value="">bitte wählen</option>
                    {nodeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              </Row>
              <BindingRow
                label="Datenpunkt (Dicke)"
                value={link.valuePoint}
                plantId={plantId}
                filter={{ valueType: 'number' }}
                onChange={(next) => patchLink({ valuePoint: next })}
              />
              <TextField
                label="Beschriftung"
                value={link.label}
                onChange={(next) => patchLink({ label: next })}
              />
            </>
          )}
        />
      </Section>
    </>
  );
}

function labelOf(nodes: readonly FlowNode[], id: string): string {
  return nodes.find((n) => n.id === id)?.label || id || '?';
}

/** Kurze, lesbare Id fuer einen neuen Knoten. Keine Guid: diese Id steht im
 *  gespeicherten JSON und wird von Hand in den Verbindungen wiedererkannt. */
function freshNodeId(nodes: readonly FlowNode[]): string {
  for (let n = 1; ; n += 1) {
    const candidate = `knoten-${n}`;
    if (!nodes.some((node) => node.id === candidate)) return candidate;
  }
}
